import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  CreditCard,
  Loader2,
  Sparkles,
  Users,
  AlertTriangle,
  FileText,
  ExternalLink,
  Download,
  ArrowDownUp,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";

function money(n, currency = "usd") {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch {
    return `$${Math.round(n)}`;
  }
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadInvoicesCsv(rows) {
  const header = [
    "Invoice number",
    "Invoice ID",
    "Date",
    "Period start",
    "Period end",
    "Amount",
    "Currency",
    "Status",
    "Hosted URL",
    "PDF URL",
  ];
  const isoDate = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
  const lines = [header.join(",")];
  for (const inv of rows) {
    const amt = inv.amount_paid || inv.amount_due || 0;
    lines.push(
      [
        inv.number || "",
        inv.id || "",
        isoDate(inv.created),
        isoDate(inv.period_start),
        isoDate(inv.period_end),
        amt.toFixed(2),
        inv.currency || "USD",
        inv.status || "",
        inv.hosted_invoice_url || "",
        inv.invoice_pdf || "",
      ]
        .map(csvEscape)
        .join(",")
    );
  }
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const today = new Date().toISOString().slice(0, 10);
  const a = document.createElement("a");
  a.href = url;
  a.download = `hireloop-invoices-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState({});
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [invFilter, setInvFilter] = useState("all"); // all|paid|open|void
  const [invRange, setInvRange] = useState("all"); // all|30d|90d|365d
  const [invSort, setInvSort] = useState("desc"); // desc|asc
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [opening, setOpening] = useState(false);
  const [billingInterval, setBillingInterval] = useState("monthly"); // monthly | annual

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
    api
      .get("/billing/summary")
      .then(({ data }) => {
        setSummary(data);
        if (data.has_customer) {
          api
            .get("/billing/invoices")
            .then(({ data }) => setInvoices(data.invoices || []))
            .catch(() => setInvoices([]));
        } else {
          setInvoices([]);
        }
      })
      .catch(() => {});
  }, []);

  const choose = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/checkout/session", {
        plan: planId,
        origin_url: window.location.origin,
        interval: billingInterval,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  const openPortal = async () => {
    setOpening(true);
    const t = toast.loading("Opening Stripe Customer Portal…");
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not open billing portal",
        { id: t }
      );
      setOpening(false);
    }
  };

  const onRealSubscription =
    !!summary && !!summary.has_subscription && !!summary.plan_id;

  const visibleInvoices = useMemo(() => {
    if (!invoices) return null;
    const cutoffMs = (() => {
      const now = Date.now();
      if (invRange === "30d") return now - 30 * 86_400_000;
      if (invRange === "90d") return now - 90 * 86_400_000;
      if (invRange === "365d") return now - 365 * 86_400_000;
      return 0;
    })();
    return invoices
      .filter((inv) => {
        if (invFilter !== "all") {
          if (invFilter === "void") {
            if (inv.status !== "void" && inv.status !== "uncollectible")
              return false;
          } else if (inv.status !== invFilter) {
            return false;
          }
        }
        if (cutoffMs > 0) {
          const t = inv.created ? new Date(inv.created).getTime() : 0;
          if (!t || t < cutoffMs) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const ta = a.created ? new Date(a.created).getTime() : 0;
        const tb = b.created ? new Date(b.created).getTime() : 0;
        return invSort === "asc" ? ta - tb : tb - ta;
      });
  }, [invoices, invFilter, invRange, invSort]);

  // ---------- Active-subscription view ----------
  if (onRealSubscription) {
    const renewal = fmtDate(summary.next_renewal);
    return (
      <main
        data-testid="billing-page"
        className="mx-auto max-w-5xl px-6 py-12"
      >
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
          Subscription
        </div>
        <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          Your subscription
        </h1>
        <p className="mt-3 max-w-xl text-base text-black">
          You're on the <strong>{summary.plan_name}</strong> plan. Update your
          card, change plan, or cancel any time via the Stripe Customer Portal.
        </p>

        {summary.billing_problem && (
          <div
            data-testid="billing-payment-issue"
            className="mt-6 flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <div className="flex-1">
              Your last invoice payment failed. Update your card to avoid
              losing access.
            </div>
          </div>
        )}

        {/* Subscription summary card */}
        <section className="mt-8 border border-slate-200 bg-white">
          <div className="grid gap-px bg-slate-100 sm:grid-cols-4">
            <div className="bg-white p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                Plan
              </div>
              <div
                data-testid="billing-active-plan-name"
                className="mt-2 font-heading text-2xl font-semibold capitalize"
              >
                {summary.plan_name}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {money(summary.plan_price, summary.currency)} /mo base ·{" "}
                <span
                  data-testid="billing-active-interval"
                  className="font-semibold"
                >
                  {(summary.interval || "monthly") === "annual"
                    ? "Annual billing"
                    : "Monthly billing"}
                </span>
              </div>
            </div>
            <div className="bg-white p-5">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                <Users className="h-3 w-3" /> Seats
              </div>
              <div
                data-testid="billing-active-seats"
                className="mt-2 font-heading text-2xl font-semibold tabular-nums"
              >
                {summary.team_size}
                <span className="text-slate-400"> / {summary.included_seats}</span>
                {summary.extra_seats > 0 && (
                  <span className="ml-2 text-xs font-medium text-amber-700">
                    +{summary.extra_seats} extra
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {summary.extra_seats > 0
                  ? `${summary.extra_seats} × ${money(
                      summary.extra_seat_price,
                      summary.currency
                    )} /seat`
                  : "Within plan limit"}
              </div>
            </div>
            <div className="bg-white p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                {(summary.interval || "monthly") === "annual"
                  ? "Annual total"
                  : "Monthly total"}
              </div>
              <div
                data-testid="billing-active-total"
                className="mt-2 font-heading text-2xl font-semibold tabular-nums text-blue-700"
              >
                {money(
                  (summary.interval || "monthly") === "annual"
                    ? summary.period_total ?? summary.total_monthly * 12
                    : summary.total_monthly,
                  summary.currency
                )}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {(summary.interval || "monthly") === "annual"
                  ? `≈ ${money(summary.total_monthly, summary.currency)}/mo · 10% off`
                  : summary.extra_seats > 0
                  ? `${money(summary.plan_price, summary.currency)} + ${money(
                      summary.extra_seats * summary.extra_seat_price,
                      summary.currency
                    )}`
                  : "Base plan only"}
              </div>
            </div>
            <div className="bg-white p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                Next renewal
              </div>
              <div
                data-testid="billing-active-renewal"
                className="mt-2 font-heading text-lg font-semibold"
              >
                {renewal || "—"}
              </div>
              <Link
                to="/team"
                className="mt-1 inline-block text-xs font-medium text-blue-700 hover:underline"
              >
                Manage team →
              </Link>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50 px-5 py-4">
            <div className="text-xs text-black">
              Need to change plan, update your card, view invoices, or cancel?
              The Stripe Customer Portal handles it all securely.
            </div>
            <Button
              data-testid="billing-portal-btn"
              disabled={opening}
              onClick={openPortal}
              className="rounded-sm bg-blue-700 hover:bg-blue-600"
            >
              {opening ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="mr-2 h-4 w-4" />
              )}
              Manage subscription
            </Button>
          </div>
        </section>

        {(summary.interval || "monthly") === "monthly" && (
          <section
            data-testid="billing-upgrade-to-annual-card"
            className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-5 py-4"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-sm bg-emerald-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800">
                Save 10%
              </div>
              <div>
                <div className="font-heading text-sm font-semibold text-slate-900">
                  Switch to annual billing and save 10%
                </div>
                <div className="mt-0.5 text-xs text-slate-600">
                  Stripe handles the prorated switch — your remaining monthly
                  credit applies to the annual invoice instantly.
                </div>
              </div>
            </div>
            <Button
              data-testid="billing-upgrade-annual-btn"
              disabled={opening}
              onClick={openPortal}
              variant="outline"
              className="rounded-sm border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            >
              {opening ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Upgrade to annual
            </Button>
          </section>
        )}

        {/* Invoices */}
        <section
          data-testid="billing-invoices-section"
          className="mt-8 border border-slate-200 bg-white"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
              <FileText className="h-3 w-3" /> Invoices
            </div>
            {invoices && invoices.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <select
                  data-testid="billing-invoices-filter-status"
                  value={invFilter}
                  onChange={(e) => setInvFilter(e.target.value)}
                  className="border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All statuses</option>
                  <option value="paid">Paid</option>
                  <option value="open">Open</option>
                  <option value="void">Void / uncollectible</option>
                </select>
                <select
                  data-testid="billing-invoices-filter-range"
                  value={invRange}
                  onChange={(e) => setInvRange(e.target.value)}
                  className="border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All time</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="365d">Last 12 months</option>
                </select>
                <button
                  type="button"
                  data-testid="billing-invoices-sort-toggle"
                  onClick={() => setInvSort((s) => (s === "desc" ? "asc" : "desc"))}
                  className="inline-flex items-center gap-1 border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                  title={invSort === "desc" ? "Newest first" : "Oldest first"}
                >
                  <ArrowDownUp className="h-3 w-3" />
                  {invSort === "desc" ? "Newest" : "Oldest"}
                </button>
                <button
                  type="button"
                  data-testid="billing-invoices-export-csv"
                  disabled={!visibleInvoices || visibleInvoices.length === 0}
                  onClick={() => {
                    if (!visibleInvoices || visibleInvoices.length === 0) return;
                    downloadInvoicesCsv(visibleInvoices);
                    toast.success(
                      `Exported ${visibleInvoices.length} invoice${
                        visibleInvoices.length === 1 ? "" : "s"
                      }`
                    );
                  }}
                  className="inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Download current view as CSV"
                >
                  <FileDown className="h-3 w-3" />
                  Export CSV
                </button>
                <span
                  data-testid="billing-invoices-count"
                  className="text-[11px] uppercase tracking-[0.18em] text-slate-500"
                >
                  {visibleInvoices?.length || 0} of {invoices.length}
                </span>
              </div>
            )}
          </div>
          {invoices === null ? (
            <div className="flex justify-center px-5 py-8">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : visibleInvoices.length === 0 ? (
            <div
              data-testid="billing-invoices-empty"
              className="px-5 py-8 text-center text-sm text-slate-500"
            >
              {invoices.length === 0
                ? "No invoices yet. Your first invoice will appear here right after your next billing cycle."
                : "No invoices match the current filters."}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {visibleInvoices.map((inv) => {
                const dateLabel = inv.created
                  ? fmtDate(inv.created)
                  : "—";
                const periodLabel =
                  inv.period_start && inv.period_end
                    ? `${fmtDate(inv.period_start)} – ${fmtDate(inv.period_end)}`
                    : null;
                const amt = inv.amount_paid || inv.amount_due || 0;
                const statusTone =
                  inv.status === "paid"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : inv.status === "open"
                    ? "border-amber-200 bg-amber-50 text-amber-700"
                    : inv.status === "void" || inv.status === "uncollectible"
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-slate-200 bg-slate-50 text-slate-700";
                return (
                  <div
                    key={inv.id}
                    data-testid={`billing-invoice-${inv.id}`}
                    className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-slate-50"
                  >
                    <div className="flex-1 min-w-[140px]">
                      <div className="text-sm font-medium text-black">
                        {dateLabel}
                      </div>
                      {periodLabel && (
                        <div className="text-xs text-slate-500">
                          {periodLabel}
                        </div>
                      )}
                    </div>
                    <div className="min-w-[80px] text-right text-sm font-semibold tabular-nums text-black">
                      {money(amt, inv.currency)}
                    </div>
                    <span
                      className={`min-w-[64px] border px-2 py-0.5 text-center text-[10px] font-bold uppercase tracking-[0.18em] ${statusTone}`}
                    >
                      {inv.status || "—"}
                    </span>
                    <div className="flex items-center gap-2">
                      {inv.hosted_invoice_url && (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`billing-invoice-view-${inv.id}`}
                          className="inline-flex items-center gap-1 border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                        >
                          <ExternalLink className="h-3 w-3" /> View
                        </a>
                      )}
                      {inv.invoice_pdf && (
                        <a
                          href={inv.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`billing-invoice-pdf-${inv.id}`}
                          className="inline-flex items-center gap-1 border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                        >
                          <Download className="h-3 w-3" /> PDF
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    );
  }

  // ---------- Pick-a-plan view (no real subscription yet) ----------
  return (
    <main data-testid="billing-page" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-12">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
        Subscription
      </div>
      <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
        {summary?.is_trial ? "Activate your subscription" : "Pick your plan"}
      </h1>
      <p className="mt-4 max-w-xl text-base text-slate-500">
        {summary?.is_trial
          ? `You're on a free trial of the ${summary.plan_name} plan${
              summary.next_renewal
                ? ` until ${fmtDate(summary.next_renewal)}`
                : ""
            }. Add a card now to keep your workspace running.`
          : "Subscribe to unlock unlimited AI interviews. Cancel any time."}
      </p>

      {user?.subscription_plan && summary?.is_trial && (
        <div className="mt-8 inline-flex items-center gap-2 border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
          <Sparkles className="h-3 w-3" />
          Trial · {summary.plan_name}
          {summary.next_renewal && (
            <span className="text-amber-600">
              · ends {fmtDate(summary.next_renewal)}
            </span>
          )}
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div
          data-testid="billing-interval-toggle"
          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white p-1 shadow-sm"
        >
          <button
            data-testid="billing-interval-monthly"
            type="button"
            onClick={() => setBillingInterval("monthly")}
            className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition ${
              billingInterval === "monthly"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Monthly
          </button>
          <button
            data-testid="billing-interval-annual"
            type="button"
            onClick={() => setBillingInterval("annual")}
            className={`relative rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition ${
              billingInterval === "annual"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Annual
            <span
              className={`ml-2 inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9px] font-bold tracking-normal ${
                billingInterval === "annual"
                  ? "bg-emerald-400 text-emerald-950"
                  : "bg-emerald-100 text-emerald-700"
              }`}
            >
              Save 10%
            </span>
          </button>
        </div>
        {plans?._promo?.active && billingInterval === "annual" && (
          <div
            data-testid="billing-annual-promo-pill"
            className="inline-flex items-center gap-1 rounded-sm border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800"
          >
            <Sparkles className="h-3 w-3" />
            Promo: {plans._promo.label || "Limited-time offer"}
          </div>
        )}
      </div>

      <div className="mt-10 grid gap-px bg-slate-200 lg:grid-cols-3">
        {Object.entries(plans)
          .filter(([k]) => !k.startsWith("_"))
          .map(([, p]) => {
          const isCurrent = user?.subscription_plan === p.id;
          const highlight = p.id === "pro";
          const isAnnual = billingInterval === "annual";
          const displayPerMonth = isAnnual
            ? p.annual_effective_monthly ?? Math.round(p.price * 0.9 * 100) / 100
            : p.price;
          const annualTotal =
            p.annual_price ?? Math.round(p.price * 12 * 0.9 * 100) / 100;
          const ctaPrice = isAnnual
            ? `${money(annualTotal, "usd")}/yr`
            : `$${p.price}/mo`;
          return (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`flex flex-col bg-white p-8 ${
                highlight ? "lg:-mt-4 lg:pb-12 lg:pt-12" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-heading text-2xl font-medium">{p.name}</div>
                {highlight && (
                  <div className="bg-blue-700 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                    Most popular
                  </div>
                )}
              </div>
              <div className="mt-4 flex items-baseline gap-1">
                <span
                  data-testid={`plan-${p.id}-price`}
                  className="font-heading text-5xl font-semibold"
                >
                  ${displayPerMonth}
                </span>
                <span className="text-sm text-slate-500">/month</span>
              </div>
              {isAnnual && (
                <div
                  data-testid={`plan-${p.id}-annual-detail`}
                  className="mt-1 text-xs text-slate-500"
                >
                  <span className="line-through opacity-60">${p.price}/mo</span>
                  <span className="ml-2 font-semibold text-emerald-700">
                    {money(annualTotal, "usd")}/yr · save 10%
                  </span>
                </div>
              )}
              <ul className="mt-6 space-y-3 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-blue-700" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`choose-plan-${p.id}-btn`}
                disabled={loadingPlan === p.id}
                onClick={() => choose(p.id)}
                className={`mt-8 rounded-sm text-white ${
                  highlight
                    ? "bg-brand-gradient"
                    : "bg-slate-900 hover:bg-slate-800"
                }`}
              >
                {loadingPlan === p.id && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isCurrent && summary?.is_trial
                  ? `Activate — ${ctaPrice}`
                  : `Subscribe — ${ctaPrice}`}
              </Button>
            </div>
          );
        })}
      </div>
    </main>
  );
}
